import { FlexBox, Skeleton } from '@dixa/lib-shared-habla';
import { AnalyticsMenu, AnalyticsMenuActions } from 'lib-analytics-ui';
import { DashboardFavorite } from 'lib-dashboard-types';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import { memo, useMemo } from 'react';

import { AppLink } from 'components/appLink';

type FavoriteDashboardFavoriteActionsProps = {
  favorite: DashboardFavorite;
  isSkeleton?: boolean;
};

export const FavoriteDashboardFavoriteActions = memo(
  function FavoriteDashboardFavoriteActions({
    favorite,
    isSkeleton,
  }: FavoriteDashboardFavoriteActionsProps): JSX.Element {
    const hasAnalyticsAccess = useHasAccess('analytics');

    const actions = useMemo(
      (): AnalyticsMenuActions => [
        {
          type: 'link',
          icon: 'conversations',
          label: 'See all conversations',
          link: (
            <AppLink
              path="/conversations"
              params={{
                search: {
                  queue: [...favorite.params.queue_ids],
                },
              }}
            />
          ),
        },

        ...(hasAnalyticsAccess
          ? ([
              {
                type: 'link',
                icon: 'chart-bar',
                label: 'Go to Analytics',
                link: (
                  <AppLink
                    path="/analytics/dashboard/:slug"
                    params={{
                      path: { slug: 'queues' },
                      search: {
                        period: 'today',
                        context: `queue_id~is_one~${favorite.params.queue_ids.join(',')}`,
                      },
                    }}
                  />
                ),
              },
            ] satisfies AnalyticsMenuActions)
          : []),

        {
          type: 'link',
          icon: 'presentation',
          label: 'TV mode',
          link: (
            <AppLink
              path="/tv/dashboard/favorites/:id"
              params={{
                path: {
                  id: favorite.id,
                },
              }}
            />
          ),
          onClick: () => {
            ampli.dashboardFavoriteTvDashboardClicked();
          },
        },
      ],
      [favorite.id, favorite.params.queue_ids, hasAnalyticsAccess],
    );

    if (isSkeleton) {
      return (
        <FlexBox alignItems="center" justifyContent="center" width="32px">
          <Skeleton height="20px" width="20px" />
        </FlexBox>
      );
    }

    return <AnalyticsMenu actions={actions} />;
  },
);
